<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import { BaseAvatar, BaseButton, getDisplayName, getInitials } from '@magma-app/magma-lapilli';

const props = defineProps<{
  helper?: {
    firstname: string;
    lastname: string;
    profilePictureUrl?: string;
  };
  organization?: {
    name: string;
    primaryColor?: string;
  };
  rgbColor?: string;
}>();

const emit = defineEmits<{ (event: 'start'): void }>();
const { t } = useI18n({ useScope: 'global' });
const safeRgbColor = computed(() => props.rgbColor || '');

const helperFullName = computed(() => {
  if (!props.helper) return '';
  return getDisplayName(props.helper.firstname, props.helper.lastname);
});

const helperFallback = computed(() => {
  if (!props.helper) return '';
  return getInitials(props.helper.firstname, props.helper.lastname);
});
</script>

<template>
  <div class="px-5 pt-12 md:px-10">
    <div
      class="absolute left-0 right-0 top-0 h-[101px] rounded-t-2xl"
      :style="{
        background: rgbColor
          ? `linear-gradient(180deg, rgba(${rgbColor}, 0.2) 0%, rgba(224, 234, 255, 0) 79.7%)`
          : `linear-gradient(180deg, #e0eaff 0%, rgba(224, 234, 255, 0) 79.7%)`
      }"
    ></div>

    <BaseAvatar
      v-if="helper"
      :src="helper.profilePictureUrl"
      :alt="`${t('generic.profilePicture')} ${helperFullName}`"
      :fallback="helperFallback"
      size="xl"
    />

    <h2 class="mt-5 text-mgm-txt-xl font-semibold text-foreground-emphasis">
      {{ helperFullName }}
    </h2>

    <p class="mt-1 text-mgm-txt-sm font-normal text-foreground-default">
      {{
        `${t('helpeeSharingView.description.one')} ${organization?.name} ${t(
          'helpeeSharingView.description.two'
        )}`
      }}
    </p>

    <BaseButton
      class="mt-5 w-full"
      :custom-color="organization?.primaryColor"
      :custom-rgb-color="safeRgbColor"
      :text="`${t('helpeeSharingView.intro')} ${helper?.firstname || ''}`"
      @click="emit('start')"
    />
  </div>
</template>
